// TODO: Review AI Code for feature: System Landscape & Interactions Visualization - Entire file is AI-generated
import { useState, useRef, useCallback, lazy, Suspense } from 'react';
import { useTranslation } from 'react-i18next';
import { Network, Link2, AlertTriangle, Unplug, Workflow, Box } from 'lucide-react';
import { ENTITY_ICONS } from '@/constants/entityIcons';
import { useSystemLandscape } from '@/api/queries/useSystemLandscape';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import { SystemTabsNav } from '@/components/systems/SystemTabsNav';
import {
  StatCard,
  Skeleton,
  Page,
  PageHeader,
  PageContent,
  VisualizationFullscreenOverlay
} from '@bliks-io/ui';
import type { SystemLandscapeResponse } from '@bliks-io/contracts';
import {
  ViewToggle,
  VisualizationToolbar,
  VisualizationFallback,
  type ViewModeOption
} from '../organization/systems/components/VisualizationControls';

const SystemLandscape2D = lazy(
  () => import('../organization/systems/components/SystemLandscape2D')
);
const SystemLandscape3D = lazy(
  () => import('../organization/systems/components/SystemLandscape3D')
);

type ViewMode = '2d' | '3d';
type LandscapeData = SystemLandscapeResponse['data'];

// ============================================================================
// KPI Cards
// ============================================================================

function KpiCards({ summary }: { summary: LandscapeData['summary'] }) {
  const { t } = useTranslation();

  return (
    <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
      <StatCard
        icon={<ENTITY_ICONS.system className="w-5 h-5 text-info" />}
        title={t('systemLandscape.kpi.systems')}
        value={summary.totalSystems}
        description={t('systemLandscape.kpi.systemsDesc')}
      />
      <StatCard
        icon={<Link2 className="w-5 h-5 text-info" />}
        title={t('systemLandscape.kpi.connections')}
        value={summary.totalConnections}
        description={t('systemLandscape.kpi.connectionsDesc')}
      />
      <StatCard
        icon={<Network className="w-5 h-5 text-info" />}
        title={t('systemLandscape.kpi.avgConnections')}
        value={summary.averageConnectionsPerSystem}
        description={t('systemLandscape.kpi.avgConnectionsDesc')}
      />
      <StatCard
        icon={<Unplug className="w-5 h-5 text-warning" />}
        title={t('systemLandscape.kpi.isolated')}
        value={summary.isolatedSystems}
        description={t('systemLandscape.kpi.isolatedDesc')}
        className={summary.isolatedSystems > 0 ? 'border-warning-line' : undefined}
      />
      <StatCard
        icon={<AlertTriangle className="w-5 h-5 text-warning" />}
        title={t('systemLandscape.kpi.mediaBreaks')}
        value={summary.totalMediaBreaks}
        description={t('systemLandscape.kpi.mediaBreaksDesc')}
        className={summary.totalMediaBreaks > 0 ? 'border-warning-line' : undefined}
      />
    </div>
  );
}

// ============================================================================
// View Mode Configuration
// ============================================================================

const VIEW_MODES: ViewModeOption<ViewMode>[] = [
  { value: '2d', icon: Workflow },
  { value: '3d', icon: Box, hideOnMobile: true, hideOnTablet: true }
];

// ============================================================================
// Main Page
// ============================================================================

/**
 * System landscape overview page with KPI summary cards and switchable
 * visualization (list / 2D graph / 3D graph) including fullscreen mode.
 */
export function SystemLandscapePage() {
  const { t } = useTranslation();
  const { data, isLoading, error } = useSystemLandscape();
  const [viewMode, setViewMode] = useState<ViewMode>('2d');
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isRotating, setIsRotating] = useState(true);
  const resetRef = useRef<(() => void) | null>(null);

  useDocumentTitle('pageTitles.systemLandscape');

  const getLabel = useCallback((mode: ViewMode) => t(`systemLandscape.view.${mode}`), [t]);

  const toggleFullscreen = useCallback(() => setIsFullscreen((v) => !v), []);

  const handleReset = useCallback(() => {
    resetRef.current?.();
  }, []);

  const handleToggleRotation = useCallback(() => setIsRotating((v) => !v), []);

  const renderVisualization = (fullscreen: boolean) =>
    data && (
      <>
        {viewMode === '2d' && (
          <Suspense fallback={<VisualizationFallback height={500} />}>
            <SystemLandscape2D data={data} onResetRef={resetRef} isFullscreen={fullscreen} />
          </Suspense>
        )}
        {viewMode === '3d' && (
          <Suspense fallback={<VisualizationFallback height={500} />}>
            <SystemLandscape3D data={data} onResetRef={resetRef} rotating={isRotating} />
          </Suspense>
        )}
      </>
    );

  const renderContent = () => {
    if (isLoading) {
      return (
        <div className="space-y-6">
          <Skeleton className="w-64 h-8" />
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
            {Array.from({ length: 5 }).map((_, i) => (
              <Skeleton key={i} className="h-28 rounded-lg" />
            ))}
          </div>
          <Skeleton className="w-full h-[500px] rounded-lg" />
        </div>
      );
    }

    if (error || !data) {
      return <p className="text-muted-foreground">{t('common.errorOccurred')}</p>;
    }

    return (
      <div className="flex flex-col flex-1 min-h-0 gap-6">
        {/* Header */}
        <div className="flex items-center justify-between shrink-0">
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Network className="w-6 h-6" />
            {t('systemLandscape.title')}
          </h1>
        </div>

        {/* KPI Cards */}
        <div className="shrink-0">
          <KpiCards summary={data.summary} />
        </div>

        {/* Visualization */}
        <div className="flex-1 min-h-0 rounded-lg border bg-card shadow-xs overflow-hidden relative">
          {!isFullscreen && renderVisualization(false)}
          <div className="absolute top-3 right-3 z-10 flex items-center gap-2">
            <VisualizationToolbar
              show3DControls={viewMode === '3d'}
              isFullscreen={false}
              isRotating={isRotating}
              onToggleRotation={handleToggleRotation}
              onReset={handleReset}
              onToggleFullscreen={toggleFullscreen}
            />
            <ViewToggle
              modes={VIEW_MODES}
              active={viewMode}
              onChange={setViewMode}
              getLabel={getLabel}
            />
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      <Page>
        <PageHeader>
          <SystemTabsNav />
        </PageHeader>
        <PageContent className="flex flex-col overflow-hidden!">{renderContent()}</PageContent>
      </Page>

      {isFullscreen && (
        <VisualizationFullscreenOverlay
          icon={Network}
          title={t('systemLandscape.title')}
          onClose={() => setIsFullscreen(false)}
          controls={
            <>
              <VisualizationToolbar
                show3DControls={viewMode === '3d'}
                isFullscreen
                isRotating={isRotating}
                onToggleRotation={handleToggleRotation}
                onReset={handleReset}
                onToggleFullscreen={toggleFullscreen}
              />
              <ViewToggle
                modes={VIEW_MODES}
                active={viewMode}
                onChange={setViewMode}
                getLabel={getLabel}
              />
            </>
          }
        >
          {renderVisualization(true)}
        </VisualizationFullscreenOverlay>
      )}
    </>
  );
}
